// Rule 4 on a screen: where a bounce or complaint rate stands, and the one
// colour that says so.
//
// CLAUDE.md, rule 4: a pool is raised only if the previous day's bounce is
// under 2 % and complaint under 0.1 %; complaint over 0.2 % or bounce over 3 %
// pauses it at once. The rule itself is `emc_db/warmup/ramp.py`, and these
// numbers are its numbers — tests/admin/test_rule4_zones.py reads both and
// fails if they part. The comparison is the rule's too: exact integer ratios
// (`bounced * 1000 < 20 * sent`), never a rounded percentage, so a cell is
// never coloured by a rounding the rule does not make.
//
// The zone words are the warmup page's (UI-6, `warmup_view.zone`), so the two
// screens say the same thing about the same day:
//
//   none     nothing was sent — not a rate of zero
//   clear    under the raise line: the warmup may raise tomorrow
//   between  at or over the raise line, not over the pause line: it holds
//   over     over the pause line: the scheduler pauses the pool
//
// Colour follows the zone and nothing else (DESIGN.md "Szín"): `over` is
// brick, `between` is amber, everything else is the ordinary grey. "Near" —
// clear, but most of the way to the raise line — is a sentence on the "what is
// red now" strip, not a colour: amber means the rule is holding, and a cell
// that is merely close must not claim that.

import type { Tone } from "@/components/emc/status";

/** Permille, as `ramp.py` states them. */
export const RULE4 = {
  raiseBounceBelow: 20,
  raiseComplaintBelow: 1,
  pauseBounceAbove: 30,
  pauseComplaintAbove: 2,
} as const;

/** `Knobs.min_sample` (EMC_WARMUP_MIN_SAMPLE): under this many sends in a day
 * the scheduler neither raises nor pauses a pool — "no evidence". A mailbox
 * provider's row is held to the same sample: one bounce in twenty-eight is a
 * 3,6% that says nothing, and colouring it would be the screen crying wolf
 * where the rule itself would stay silent. */
export const POOL_MIN_SAMPLE = 200;

/** "Near": at least this share of the way to the raise line. */
export const NEAR_SHARE = 0.75;

export type Rule4Metric = "bounce" | "complaint";
export type Zone = "none" | "clear" | "between" | "over";

interface Lines {
  below: number;
  above: number;
}

const LINES: Record<Rule4Metric, Lines> = {
  bounce: { below: RULE4.raiseBounceBelow, above: RULE4.pauseBounceAbove },
  complaint: { below: RULE4.raiseComplaintBelow, above: RULE4.pauseComplaintAbove },
};

const METRIC_WORD: Record<Rule4Metric, string> = {
  bounce: "bounce",
  complaint: "panasz",
};

/** Where one rate stands, compared the way `ramp.clear_to_raise` and
 * `ramp.over_pause_threshold` compare. */
export function zone(part: number, sent: number, below: number, above: number): Zone {
  if (sent <= 0) return "none";
  if (part * 1000 > above * sent) return "over";
  if (part * 1000 < below * sent) return "clear";
  return "between";
}

export interface RateVerdict {
  metric: Rule4Metric;
  part: number;
  sent: number;
  zone: Zone;
  /** Clear, but at least NEAR_SHARE of the way to the raise line. */
  near: boolean;
  /** Fewer sends than the judgement needs: shown, never coloured. */
  small: boolean;
  tone: Tone;
  /** The rate in percent, for display; null when nothing was sent. */
  percent: number | null;
  /** The two lines in percent, for the sentence beside the number. */
  raiseLine: number;
  pauseLine: number;
}

/** One rate against rule 4, below `minSample` sends shown and never coloured. */
export function judge(
  metric: Rule4Metric,
  part: number,
  sent: number,
  minSample: number = POOL_MIN_SAMPLE,
): RateVerdict {
  const lines = LINES[metric];
  const where = zone(part, sent, lines.below, lines.above);
  const small = sent > 0 && sent < minSample;
  // Exact, like the zone: part / sent >= NEAR_SHARE * below / 1000.
  const near = where === "clear" && part * 1000 >= NEAR_SHARE * lines.below * sent && part > 0;
  let tone: Tone = "muted";
  if (!small && where === "over") tone = "danger";
  else if (!small && where === "between") tone = "warn";
  return {
    metric,
    part,
    sent,
    zone: where,
    near,
    small,
    tone,
    percent: sent > 0 ? (part / sent) * 100 : null,
    raiseLine: lines.below / 10,
    pauseLine: lines.above / 10,
  };
}

/** "0,08%": two decimals under one percent, one above — a complaint rate is
 * read in hundredths (0,1% is the line), a bounce rate in tenths. Fixed
 * decimals, so a column of rates lines up; no space before the sign, the
 * way `format.share` writes a share everywhere else in the admin. */
export function percentText(value: number | null): string {
  if (value === null) return "—";
  // 0,996 shows as "1,0%", not "1,00%": the decimals follow the rounded value.
  const digits = value === 0 ? 0 : Math.abs(value) < 0.995 ? 2 : 1;
  return `${value.toLocaleString("hu-HU", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;
}

/** `part` of `whole` as `percentText`, or "—" when nothing was sent. */
export function rateText(part: number, whole: number): string {
  return whole > 0 ? percentText((part / whole) * 100) : "—";
}

function lineText(value: number): string {
  return `${value.toLocaleString("hu-HU", { maximumFractionDigits: 2 })}%`;
}

/** The sentence beside a coloured number: what it is measured against, and
 * what the rule does with it (DESIGN.md "Szövegírás"). */
export function verdictText(verdict: RateVerdict, minSample: number = POOL_MIN_SAMPLE): string {
  const word = METRIC_WORD[verdict.metric];
  const rate = percentText(verdict.percent);
  if (verdict.zone === "none") return `${word}: nem ment ki levél`;
  if (verdict.small) {
    return `${word} ${rate} — ${verdict.sent} küldésből; ${minSample} alatt a szabály nem dönt`;
  }
  switch (verdict.zone) {
    case "over":
      return `${word} ${rate} — a megállítási küszöb (${lineText(verdict.pauseLine)}) fölött`;
    case "between":
      return `${word} ${rate} — az emelési határ (${lineText(verdict.raiseLine)}) fölött: a warmup tart`;
    default:
      return verdict.near
        ? `${word} ${rate} — közel az emelési határhoz (${lineText(verdict.raiseLine)})`
        : `${word} ${rate} — az emelési határ (${lineText(verdict.raiseLine)}) alatt`;
  }
}

/** Said after a provider's rate that crosses a line: rule 4 pauses and holds
 * on the pool's figures, so one provider over the line is a signal, not a stop. */
export const PROVIDER_NOTE = "szolgáltatónként csak jelzés: a 4. szabály a pool egészére dönt";

/** Worst first: over, between, near, the rest. For sorting a strip. */
export function severity(verdict: RateVerdict): number {
  if (verdict.small) return verdict.zone === "over" ? 1 : 0;
  if (verdict.zone === "over") return 4;
  if (verdict.zone === "between") return 3;
  if (verdict.near) return 2;
  return 0;
}
